import {
  faExclamationTriangle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import * as Config from '../../../public/external-links'
import Accordion from '../../common/Accordion'
import GreyBox from '../../common/GreyBox'
import PageTitle from '../../PageTitle'
import { ICallout } from '../../../models/Callout'
import useCallout from '../../../hooks/useCallout'
import LoaderContext from '../../context/FormLoader'
import Conditional from '../../common/Conditional'
import CalloutDisplay from '../../common/CalloutDisplay'
import DocumentLink from '../../common/DocumentLink'

export const AnnualLeaveNoticeHeader = (props) => {
  const mountedRef = useRef(false)
  const [data, setData] = useState<ICallout[]>([])
  const [getCallout] = useCallout('AnnualLeaveNoticeTemplate', 'Before')
  const [isLoading, setIsLoading] = useState(true)
  const loader = useContext(LoaderContext)

  useEffect(() => {
    mountedRef.current = true
    loader.up()
    if (mountedRef.current) {
      getCallout()
        .then((response) => {
          if (mountedRef.current) {
            setData(response.callouts)
          }
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          setIsLoading(false)
          loader.down()
        })
    }
    return () => {
      mountedRef.current = false
    }
  }, [])
  return (
    <>
      <PageTitle title="Notice of requirement to take annual leave - Templates - Fair Work Ombudsman" />
      <h1>Notice of requirement to take annual leave</h1>
      <Accordion
        heading="Welcome to our Template Tool"
        expanded={props.show}
        onClick={() => props.setShow()}
      >
        <Conditional if={!isLoading}>
          {data &&
            data.map((c, i) => (
              <CalloutDisplay
                key={i}
                className=""
                content={c.content}
                name="AnnualLeaveNotice"
              />
            ))}
        </Conditional>
        <p>
          This tool will help you prepare a letter directing your employee to
          take annual leave. It covers temporary business shutdowns and
          excessive leave balances.
        </p>
        <p>
          The tool will ask you simple questions and use your answers to tailor
          a letter for you. You will be able to save and edit your letter at the
          end.
        </p>
        <h2>Important information</h2>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faExclamationTriangle}
            size={'1x'}
            color={'#da291c'}
          />
          &nbsp;buttons for important information about workplace rights and
          obligations
        </p>
        <h2>Get help</h2>
        <p>
          Click the&nbsp;
          <FontAwesomeIcon
            className="ml-xs"
            icon={faInfoCircle}
            size={'lg'}
            color={'#1b365d'}
          />
          &nbsp;buttons for explanations or help answering the question
        </p>
        <p>
          To protect your privacy none of the information you enter is stored in
          this tool or captured by the Office of the Fair Work Ombudsman.
        </p>

        <GreyBox>
          <p>
            <strong>Before you begin:</strong> To prepare your letter you’ll
            need to know if an award or enterprise agreement applies to the
            employee and, if so, ensure that it allows you to direct the
            employee to take annual leave.
          </p>
          <p>All fields are required unless marked optional.</p>
        </GreyBox>
        <Button className="ui is-primary button" onClick={props.handleStart}>
          Start
        </Button>

        <p>
          If you don’t wish to use this tool, you can download and complete one
          of the following blank template letters:
        </p>
        <ul>
          <li>
            <DocumentLink
              name="Notice of requirement to take annual leave for temporary shutdown"
              urlDoc={Config.INTERACTIVE_TEMPLATEA_ANNUAL_LEAVE_NOTICE_DOCX}
              urlPdf={Config.INTERACTIVE_TEMPLATEA_ANNUAL_LEAVE_NOTICE_PDF}
              sizeDoc=" 65KB"
              sizePdf=" 301.3KB"
            />
          </li>
          <li>
            <DocumentLink
              name="Direction to take excessive annual leave"
              urlDoc={Config.INTERACTIVE_TEMPLATEA_EXCESSIVE_ANNUAL_LEAVE_DOCX}
              urlPdf={Config.INTERACTIVE_TEMPLATEA_EXCESSIVE_ANNUAL_LEAVE_PDF}
              sizeDoc=" 51.9KB"
              sizePdf=" 427.3KB"
            />
          </li>
        </ul>
      </Accordion>
    </>
  )
}
